// The canvas's toolbar: add a tile, and everything that happens to a dashboard
// once it exists.
//
// A stale save (409) offers exactly two recoveries: load THEIR version (losing
// my edits, which the panel says) or keep MINE as a new dashboard. It never
// re-issues the save with a bumped version: that is last-write-wins.
//
// Presentational only. Every action is a callback the page owns, so the
// mutations, their invalidations and their error handling live where the
// queries do.
import { Button, Input } from "@bi/shared";
import type { StudioVersionEntry } from "@bi/shared/types";

import { CHART_TILE_KINDS, TILE_KIND_LABELS, type TileKind } from "../../lib/studio-model";

export interface SaveBarProps {
  title: string;
  onTitleChange: (title: string) => void;
  /** `null` until the first save. Most actions need a stored row. */
  dashboardId: string | null;
  isOwner: boolean;
  shared: boolean;
  dirty: boolean;
  saving: boolean;
  /** The version currently loaded, shown so a person can see a save land. */
  version: number;
  onAddTile: (kind: TileKind) => void;
  onAddText: () => void;
  onAddControl: () => void;
  /** Undo and redo over the document. Disabled rather than hidden at the ends
   *  of history, so the pair does not move around under the pointer. */
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Copy the selected tile in beside itself. `null` when nothing is selected:
   *  the button is shown disabled rather than hidden, because a control that
   *  appears and disappears with the selection is one people stop looking
   *  for. */
  onDuplicateTile: (() => void) | null;
  /** The experiment this board is filed under and currently reads, and the way
   *  to point it at another one. `null` for a board that is not a template — in
   *  which case the bar says so rather than offering a picker that would change
   *  nothing. */
  experiment: {
    key: string | null;
    options: readonly string[];
    onRebind: (key: string) => void;
  } | null;
  onSave: () => void;
  onShareChange: (shared: boolean) => void;
  onFork: () => void;
  onDelete: () => void;
  onExport: () => void;
  onToggleVersions: () => void;
  versionsOpen: boolean;
  versions: readonly StudioVersionEntry[];
  onRestore: (version: number) => void;
  /** Set when a save came back 409. */
  conflict: { currentVersion: number } | null;
  onReloadSaved: () => void;
  onSaveAsCopy: () => void;
  /** The export's files, `{path: text}`, offered as text to copy. */
  exportFiles: Readonly<Record<string, string>> | null;
  onDismissExport: () => void;
  error: string | null;
}

export function SaveBar(props: SaveBarProps) {
  const {
    title,
    onTitleChange,
    dashboardId,
    isOwner,
    shared,
    dirty,
    saving,
    version,
    onAddTile,
    onAddText,
    onAddControl,
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    onDuplicateTile,
    experiment,
    onSave,
    onShareChange,
    onFork,
    onDelete,
    onExport,
    onToggleVersions,
    versionsOpen,
    versions,
    onRestore,
    conflict,
    onReloadSaved,
    onSaveAsCopy,
    exportFiles,
    onDismissExport,
    error,
  } = props;

  return (
    <div className="bi-studio-bar">
      <div className="bi-studio-bar__row">
        <Input
          aria-label="Dashboard title"
          className="bi-studio-bar__title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
        />
        <span className="bi-studio-bar__version">
          {dashboardId ? `Version ${version}${dirty ? ", unsaved changes" : ""}` : "Not saved yet"}
        </span>
        <div className="bi-studio-bar__spacer" />
        <Button
          variant="solid"
          tone="accent"
          size="sm"
          disabled={saving || (!dirty && dashboardId !== null) || !isOwner}
          onClick={onSave}
        >
          {saving ? "Saving…" : dashboardId ? "Save" : "Save dashboard"}
        </Button>
        {dashboardId ? (
          <>
            <label className="bi-studio-check">
              <input
                type="checkbox"
                checked={shared}
                disabled={!isOwner}
                onChange={(e) => onShareChange(e.target.checked)}
              />
              <span>Shared</span>
            </label>
            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              title="Make a private copy you own. The copy is never shared, whatever this one is."
              onClick={onFork}
            >
              Duplicate
            </Button>
            <Button variant="outline" tone="neutral" size="sm" onClick={onToggleVersions}>
              {versionsOpen ? "Hide versions" : "Versions"}
            </Button>
            <Button variant="outline" tone="neutral" size="sm" onClick={onExport}>
              Export YAML
            </Button>
            {isOwner ? (
              <Button variant="outline" tone="danger" size="sm" onClick={onDelete}>
                Delete
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="bi-studio-bar__row">
        <span className="bi-filter__label">Add</span>
        {CHART_TILE_KINDS.map((kind) => (
          <Button
            key={kind}
            variant="outline"
            tone="neutral"
            size="sm"
            onClick={() => onAddTile(kind)}
          >
            {TILE_KIND_LABELS[kind]}
          </Button>
        ))}
        <Button variant="outline" tone="neutral" size="sm" onClick={onAddText}>
          Text
        </Button>
        <Button variant="outline" tone="neutral" size="sm" onClick={onAddControl}>
          Control
        </Button>
        <div className="bi-studio-bar__spacer" />
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={!onDuplicateTile}
          title="Copy the selected tile in beside itself"
          onClick={() => onDuplicateTile?.()}
        >
          Duplicate tile
        </Button>
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          disabled={!canUndo}
          title="Undo the last edit"
          onClick={onUndo}
        >
          Undo
        </Button>
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          disabled={!canRedo}
          title="Redo"
          onClick={onRedo}
        >
          Redo
        </Button>
      </div>

      {/* The template row. A board with an experiment control reads as a
          TEMPLATE: the same tiles answer for another experiment by rebinding one
          parameter, with no copy to keep in step. A board without one says so,
          rather than offering a picker that would change nothing. */}
      <div className="bi-studio-bar__row">
        <span className="bi-filter__label">Experiment</span>
        {experiment ? (
          <>
            <select
              className="bi-studio-select"
              aria-label="Read this dashboard for another experiment"
              value={experiment.key ?? ""}
              onChange={(e) => experiment.onRebind(e.target.value)}
            >
              <option value="">Every experiment</option>
              {experiment.options.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
            <span className="bi-studio-bar__version">
              Rebinding changes what these tiles read. Save to keep it, or fork first to keep
              both.
            </span>
          </>
        ) : (
          <span className="bi-studio-bar__version">
            This board has no experiment control, so it reads across every experiment. Add a
            control and give it the URL parameter "experiment" to make it a template.
          </span>
        )}
      </div>

      {conflict ? (
        <div className="bi-studio-conflict" role="alert">
          <p>
            Somebody else saved this dashboard while you were editing. The stored version is now{" "}
            {conflict.currentVersion}, and yours was {version}. Saving over it would drop their
            work, so it has not been saved.
          </p>
          <div className="bi-studio-bar__row">
            <Button variant="outline" tone="neutral" size="sm" onClick={onReloadSaved}>
              Load their version, discarding my changes
            </Button>
            <Button variant="solid" tone="accent" size="sm" onClick={onSaveAsCopy}>
              Keep my changes as a new dashboard
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="bi-studio-error" role="alert">
          {error}
        </p>
      ) : null}

      {versionsOpen ? (
        <div className="bi-studio-versions">
          {versions.length === 0 ? (
            <p className="bi-studio-field__hint">No earlier versions yet.</p>
          ) : (
            <ul>
              {versions.map((entry) => (
                <li key={entry.version}>
                  <span>
                    Version {entry.version}, saved by {entry.savedBy} on{" "}
                    {new Date(entry.savedAt).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    disabled={!isOwner}
                    onClick={() => onRestore(entry.version)}
                  >
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <p className="bi-studio-field__hint">
            Restoring saves the old spec as a NEW version on top of this one, so the restore
            itself can be undone.
          </p>
        </div>
      ) : null}

      {exportFiles ? (
        <div className="bi-studio-export">
          <div className="bi-studio-bar__row">
            <strong>Paste these into superset/ and open a pull request.</strong>
            <div className="bi-studio-bar__spacer" />
            <Button variant="ghost" tone="neutral" size="sm" onClick={onDismissExport}>
              Close
            </Button>
          </div>
          {/* Offered as text, not as a download: this app is viewed in a sandbox
              that blocks a page-initiated save, and a download button that
              silently does nothing is worse than a box you can select. */}
          {Object.entries(exportFiles).map(([path, text]) => (
            <label key={path} className="bi-studio-field">
              <span className="bi-studio-field__label">{path}</span>
              <textarea className="bi-studio-textarea" readOnly rows={10} value={text} />
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
